/**
 * Where to send someone after logging in. The login page knows the target from
 * `?redirect=`, but the magic link in the e-mail opens `/auth/complete` in a new
 * tab, without that query and without the original tab's sessionStorage. So the
 * target is kept briefly in localStorage, shared by all tabs on this origin.
 */

const KEY = 'chargee_postLoginRedirect'
/** Long enough to find the e-mail and click it; short enough not to hijack a later login. */
const TTL_MS = 30 * 60 * 1000

interface StoredRedirect {
  path: string
  exp: number
}

/**
 * An in-app path, or null. Anything that could leave the site is refused:
 * absolute URLs, protocol-relative `//host` and the `/\host` variant browsers
 * treat the same way.
 */
export function sanitizeRedirect(path: string | null | undefined): string | null {
  if (!path || !path.startsWith('/')) return null
  if (path.startsWith('//') || path.startsWith('/\\')) return null
  if (/[\u0000-\u001f]/.test(path)) return null
  return path
}

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** Remember the target for the magic-link tab; null clears any earlier one. */
export function savePostLoginRedirect(path: string | null, now = Date.now(), store = storage()) {
  if (!store) return
  try {
    const safe = sanitizeRedirect(path)
    if (!safe) {
      store.removeItem(KEY)
      return
    }
    const value: StoredRedirect = { path: safe, exp: now + TTL_MS }
    store.setItem(KEY, JSON.stringify(value))
  } catch {
    // Storage full or blocked: the user simply lands on the default page.
  }
}

/** Read the target once; it is removed whether or not it is still valid. */
export function consumePostLoginRedirect(now = Date.now(), store = storage()): string | null {
  if (!store) return null
  try {
    const raw = store.getItem(KEY)
    store.removeItem(KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<StoredRedirect>
    if (typeof value.exp !== 'number' || value.exp < now) return null
    return sanitizeRedirect(value.path)
  } catch {
    return null
  }
}
